/*
  Homepage hero sparkle overlay (sections/slideshow.liquid, "Enable sparkle
  effects" setting). A single pointer-events: none <canvas> over the
  slideshow draws:

  - a gold sparkle trail behind the cursor (fine pointers only), with the
    spawn rate scaled by cursor speed - nothing spawns while it's still
  - ambient gold dust drifting upward (the only continuous effect on touch)
  - a small sparkle burst on click/tap

  The crescent glow is CSS (.hero-sparkles__glow in section-slideshow.css):
  its breathing is a compositor-only opacity animation, and this script only
  nudges its opacity up as the cursor nears the hero's center.

  Loaded deferred and started after window load + idle so it never competes
  with the LCP hero image. Never starts under prefers-reduced-motion. Add
  ?hero-sparkles-debug to the URL to log the average main-thread cost per
  frame to the console.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches) return;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DEBUG = /[?&]hero-sparkles-debug\b/.test(window.location.search);

  const TAU = Math.PI * 2;
  const SPRITE_SIZE = 64; // px - sprites are pre-rendered once, drawn scaled down
  const STAR_EXTENT = 0.62; // star tip-to-tip as a fraction of the sprite
  const DOT_EXTENT = 0.3; // dot diameter as a fraction of the sprite

  const random = (min, max) => min + Math.random() * (max - min);

  // Theme colors are stored as "r, g, b" triplets (see base.css /
  // css-variables.liquid); fall back to the brand hex values if missing.
  function readRgb(styles, name, fallback) {
    const value = styles.getPropertyValue(name).trim();
    return /^\d+\s*,\s*\d+\s*,\s*\d+$/.test(value) ? value : fallback;
  }

  // Same 4-point star as snippets/icon-sparkle.liquid (inner radius ~27% of outer).
  function traceStar(ctx, cx, cy, radius) {
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4 - Math.PI / 2;
      const r = i % 2 === 0 ? radius : radius * 0.27;
      ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
    }
    ctx.closePath();
  }

  // Each particle is one drawImage() of a pre-rendered sprite (soft halo +
  // shape) instead of building a path and gradient per particle per frame.
  function makeSprite(rgb, shape) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SPRITE_SIZE;
    const ctx = canvas.getContext('2d');
    const c = SPRITE_SIZE / 2;

    const halo = ctx.createRadialGradient(c, c, 0, c, c, c);
    halo.addColorStop(0, `rgba(${rgb}, 0.45)`);
    halo.addColorStop(0.35, `rgba(${rgb}, 0.12)`);
    halo.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);

    ctx.fillStyle = `rgb(${rgb})`;
    if (shape === 'star') {
      traceStar(ctx, c, c, c * STAR_EXTENT);
    } else {
      ctx.beginPath();
      ctx.arc(c, c, c * DOT_EXTENT, 0, TAU);
    }
    ctx.fill();

    return canvas;
  }

  class HeroSparkles {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('.hero-sparkles__canvas');
      this.glow = root.querySelector('.hero-sparkles__glow');
      this.ctx = this.canvas.getContext('2d');

      this.isFine = finePointer.matches;
      this.maxParticles = this.isFine ? 120 : 40;
      this.dustCount = this.isFine ? 32 : 16;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);

      const styles = getComputedStyle(document.documentElement);
      const gold = readRgb(styles, '--color-base-accent', '189, 156, 93');
      const lightGold = readRgb(styles, '--color-accent-light', '248, 220, 165');
      const mauve = readRgb(styles, '--color-text-muted', '152, 129, 145');
      this.sprites = {
        star: { gold: makeSprite(gold, 'star'), light: makeSprite(lightGold, 'star'), mauve: makeSprite(mauve, 'star') },
        dot: { gold: makeSprite(gold, 'dot'), light: makeSprite(lightGold, 'dot'), mauve: makeSprite(mauve, 'dot') }
      };

      this.particles = [];
      this.pool = [];
      this.dustLive = 0;
      this.sparkCount = 0;
      this.width = 0;
      this.height = 0;

      this.isVisible = false;
      this.rafId = 0;
      this.lastFrame = 0;
      this.glowBoost = -1;

      // Pointer state, in client coordinates - converted to canvas space once
      // per frame so a scroll with a still cursor doesn't read as movement.
      this.pointer = { inside: false, x: 0, y: 0, prevX: 0, prevY: 0, hasPrev: false, lastMove: 0, carry: 0 };
      this.down = null;

      this.frame = this.frame.bind(this);
      this.abort = new AbortController();
      const listen = { signal: this.abort.signal, passive: true };

      if (this.isFine) {
        root.addEventListener('pointermove', (event) => this.onPointerMove(event), listen);
        root.addEventListener('pointerleave', () => this.onPointerLeave(), listen);
      }
      root.addEventListener('pointerdown', (event) => {
        this.down = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, listen);
      root.addEventListener('pointerup', (event) => this.onPointerUp(event), listen);
      root.addEventListener('pointercancel', () => { this.down = null; }, listen);
      document.addEventListener('visibilitychange', () => this.start(), { signal: this.abort.signal });

      this.resizeObserver = new ResizeObserver((entries) => this.resize(entries[0].contentRect));
      this.resizeObserver.observe(root);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isVisible = entries[0].isIntersecting;
        this.glow?.classList.toggle('is-paused', !this.isVisible);
        this.start();
      });
      this.intersectionObserver.observe(root);

      this.glow?.classList.add('is-active');

      if (DEBUG) this.debug = { time: 0, max: 0, frames: 0 };
    }

    destroy() {
      this.abort.abort();
      this.resizeObserver.disconnect();
      this.intersectionObserver.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = 0;
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.glow?.classList.remove('is-active', 'is-paused');
      this.glow?.style.removeProperty('opacity');
    }

    resize({ width, height }) {
      if (width === 0 || height === 0) return;
      const firstSize = this.width === 0;

      this.width = width;
      this.height = height;
      this.canvas.width = Math.round(width * this.dpr);
      this.canvas.height = Math.round(height * this.dpr);
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

      if (firstSize) {
        for (let i = 0; i < this.dustCount; i++) this.spawnDust(true);
      }
      this.start();
    }

    // Runs while the hero is on screen and the tab is visible. Ambient dust
    // keeps it going; spark-free, cursor-idle frames are throttled to ~30fps.
    start() {
      if (this.rafId || !this.isVisible || document.hidden || this.width === 0) return;
      this.lastFrame = performance.now();
      this.rafId = requestAnimationFrame(this.frame);
    }

    onPointerMove(event) {
      if (event.pointerType !== 'mouse') return;
      const p = this.pointer;
      p.inside = true;
      p.x = event.clientX;
      p.y = event.clientY;
      p.lastMove = performance.now();
      this.start();
    }

    onPointerLeave() {
      this.pointer.inside = false;
      this.pointer.hasPrev = false;
    }

    onPointerUp(event) {
      const down = this.down;
      this.down = null;
      if (!down || down.id !== event.pointerId) return;
      // Treat as a click/tap only if the pointer barely moved (not a Flickity drag).
      if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > 10) return;

      const rect = this.root.getBoundingClientRect();
      this.burst(event.clientX - rect.left, event.clientY - rect.top);
      this.start();
    }

    // Dust slots are reserved, so a long trail can't starve the ambient dust.
    acquire(isDust) {
      if (!isDust && this.sparkCount >= this.maxParticles - this.dustCount) return null;
      if (isDust) this.dustLive++;
      else this.sparkCount++;
      const particle = this.pool.pop() || {};
      this.particles.push(particle);
      return particle;
    }

    pickColor(set) {
      const roll = Math.random();
      if (roll < 0.08) return set.mauve;
      return roll < 0.55 ? set.gold : set.light;
    }

    spawnDust(scatter) {
      if (this.dustLive >= this.dustCount) return;
      const p = this.acquire(true);
      const size = random(1, 2.4);
      p.dust = true;
      p.x = random(0, this.width);
      p.y = scatter ? random(0, this.height) : this.height + 6;
      p.vx = 0;
      p.vy = -random(6, 20);
      p.swayAmp = random(4, 12);
      p.swaySpeed = random(0.2, 0.6);
      p.baseAlpha = random(0.25, 0.7);
      p.age = 0;
      p.life = Infinity;
      p.twinkleSpeed = random(1, 3);
      p.phase = random(0, TAU);
      p.sprite = Math.random() < 0.5 ? this.sprites.dot.gold : this.sprites.dot.light;
      p.drawSize = size / DOT_EXTENT;
    }

    spawnSpark(x, y, vx, vy, life) {
      const p = this.acquire(false);
      if (!p) return;
      const isStar = Math.random() < 0.45;
      const size = isStar ? random(4, 9) : random(1, 3);
      p.dust = false;
      p.x = x;
      p.y = y;
      p.vx = vx;
      p.vy = vy;
      p.baseAlpha = 1;
      p.age = 0;
      p.life = life;
      p.twinkleSpeed = random(6, 14);
      p.phase = random(0, TAU);
      p.sprite = this.pickColor(isStar ? this.sprites.star : this.sprites.dot);
      p.drawSize = size / (isStar ? STAR_EXTENT : DOT_EXTENT);
    }

    burst(x, y) {
      const count = 12;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * TAU + random(-0.25, 0.25);
        const speed = random(50, 130);
        this.spawnSpark(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, random(0.7, 1.1));
      }
    }

    // Converts cursor travel since the last frame into trail particles,
    // spread along the path so fast strokes leave a continuous trail.
    spawnTrail(rect, dt) {
      const p = this.pointer;
      const x = p.x - rect.left;
      const y = p.y - rect.top;

      if (!p.hasPrev) {
        p.prevX = x;
        p.prevY = y;
        p.hasPrev = true;
        return;
      }

      const dx = x - p.prevX;
      const dy = y - p.prevY;
      const distance = Math.hypot(dx, dy);
      p.prevX = x;
      p.prevY = y;
      if (distance < 0.5) return;

      p.carry = Math.min(p.carry + distance * 0.09, 6); // ~1 particle per 11px of travel
      const count = Math.floor(p.carry);
      p.carry -= count;

      const followX = (dx / dt) * 0.04;
      const followY = (dy / dt) * 0.04;
      for (let i = 0; i < count; i++) {
        const t = (i + Math.random()) / count;
        this.spawnSpark(
          x - dx * (1 - t) + random(-3, 3),
          y - dy * (1 - t) + random(-3, 3),
          followX + random(-14, 14),
          followY + random(-22, 6),
          random(0.8, 1.2)
        );
      }
    }

    updateGlow(rect) {
      if (!this.glow) return;
      let boost = 0;
      const p = this.pointer;
      if (p.inside) {
        const dx = p.x - rect.left - this.width / 2;
        const dy = p.y - rect.top - this.height / 2;
        const reach = Math.min(this.width, this.height) * 0.6;
        boost = Math.max(0, 1 - Math.hypot(dx, dy) / reach);
      }
      if (Math.abs(boost - this.glowBoost) < 0.02) return;
      this.glowBoost = boost;
      this.glow.style.opacity = (0.65 + 0.35 * boost).toFixed(2);
    }

    frame(now) {
      this.rafId = 0;
      if (!this.isVisible || document.hidden) return;

      const p = this.pointer;
      const cursorActive = p.inside && now - p.lastMove < 120;
      const hasSparks = this.sparkCount > 0;
      if (!cursorActive) p.hasPrev = false;

      // Dust alone drifts slowly enough that ~30fps looks identical.
      if (!cursorActive && !hasSparks && now - this.lastFrame < 32) {
        this.rafId = requestAnimationFrame(this.frame);
        return;
      }

      const workStart = DEBUG ? performance.now() : 0;
      const dt = Math.min((now - this.lastFrame) / 1000, 0.05); // clamp after pauses
      this.lastFrame = now;
      const time = now / 1000;

      if (p.inside || this.glowBoost > 0) {
        const rect = this.root.getBoundingClientRect();
        if (cursorActive) this.spawnTrail(rect, Math.max(dt, 0.008));
        this.updateGlow(rect);
      }

      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.globalCompositeOperation = 'lighter';

      const drag = Math.exp(-2.2 * dt);
      const particles = this.particles;
      let write = 0;
      let deadDust = 0;

      for (let read = 0; read < particles.length; read++) {
        const particle = particles[read];
        particle.age += dt;

        let alpha;
        if (particle.dust) {
          particle.y += particle.vy * dt;
          const x = particle.x + Math.sin(time * particle.swaySpeed + particle.phase) * particle.swayAmp;
          if (particle.y < -6) {
            this.pool.push(particle);
            this.dustLive--;
            deadDust++;
            continue;
          }
          const fadeIn = Math.min(particle.age / 1.5, 1);
          alpha = particle.baseAlpha * fadeIn * (0.55 + 0.45 * Math.sin(time * particle.twinkleSpeed + particle.phase));
          this.draw(particle, x, particle.y, alpha);
        } else {
          const t = particle.age / particle.life;
          if (t >= 1) {
            this.pool.push(particle);
            this.sparkCount--;
            continue;
          }
          particle.vx *= drag;
          particle.vy = particle.vy * drag - 12 * dt; // slight upward float
          particle.x += particle.vx * dt;
          particle.y += particle.vy * dt;
          const fade = (1 - t) * (1 - t);
          alpha = fade * (0.6 + 0.4 * Math.sin(particle.age * particle.twinkleSpeed + particle.phase));
          this.draw(particle, particle.x, particle.y, alpha);
        }

        particles[write++] = particle;
      }
      particles.length = write;
      ctx.globalAlpha = 1;

      for (let i = 0; i < deadDust; i++) this.spawnDust(false);

      if (DEBUG) this.logDebug(performance.now() - workStart);

      this.rafId = requestAnimationFrame(this.frame);
    }

    draw(particle, x, y, alpha) {
      if (alpha <= 0.01) return;
      const size = particle.drawSize;
      this.ctx.globalAlpha = alpha;
      this.ctx.drawImage(particle.sprite, x - size / 2, y - size / 2, size, size);
    }

    logDebug(ms) {
      const d = this.debug;
      d.time += ms;
      d.max = Math.max(d.max, ms);
      d.frames++;
      if (d.frames < 120) return;
      console.info(`[hero-sparkles] avg ${(d.time / d.frames).toFixed(3)}ms, max ${d.max.toFixed(3)}ms per frame, ${this.particles.length} particles`);
      d.time = d.max = d.frames = 0;
    }
  }

  function initAll(scope = document) {
    if (reducedMotion.matches) return;
    scope.querySelectorAll('[data-hero-sparkles]').forEach((root) => {
      if (!root.heroSparkles) root.heroSparkles = new HeroSparkles(root);
    });
  }

  function destroyAll(scope = document) {
    scope.querySelectorAll('[data-hero-sparkles]').forEach((root) => {
      root.heroSparkles?.destroy();
      delete root.heroSparkles;
    });
  }

  function scheduleInit() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initAll(), { timeout: 3000 });
    } else {
      setTimeout(() => initAll(), 200);
    }
  }

  if (document.readyState === 'complete') {
    scheduleInit();
  } else {
    window.addEventListener('load', scheduleInit, { once: true });
  }

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) destroyAll();
  });

  // Theme editor: re-init when the section is re-rendered, tear down on removal.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
